import React, { useEffect, useState } from 'react';
import { Announcement } from '../../types/announcements/announcements';
import { getAnnouncements } from '../../services/announcements/Announcements';
import { useApiClient } from '../../context/ApiContext';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface AnnouncementsCardProps {
    loginName: string;
    hasBlockingSuggestions?: boolean;
}

const AnnouncementsCard: React.FC<AnnouncementsCardProps> = ({ loginName, hasBlockingSuggestions = false }) => {
    const api = useApiClient();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [popupAnnouncements, setPopupAnnouncements] = useState<Announcement[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const VIEWED_ANNOUNCEMENTS_KEY = 'viewedAnnouncements';

    const getViewedAnnouncements = (): number[] => {
        try {
            const stored = localStorage.getItem(VIEWED_ANNOUNCEMENTS_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading viewed announcements from localStorage:', error);
            return [];
        }
    };

    const markMultipleAnnouncementsAsViewed = (announcementIds: number[]) => {
        try {
            const viewedIds = getViewedAnnouncements();
            const newViewedIds = [...viewedIds];
            announcementIds.forEach(id => {
                if (!newViewedIds.includes(id)) newViewedIds.push(id);
            });
            localStorage.setItem(VIEWED_ANNOUNCEMENTS_KEY, JSON.stringify(newViewedIds));
        } catch (error) {
            console.error('Error saving viewed announcements to localStorage:', error);
        }
    };

    const handleModalClose = () => {
        if (popupAnnouncements.length > 0) {
            markMultipleAnnouncementsAsViewed(popupAnnouncements.map(a => a.Id));
        }
        setIsModalOpen(false);
        setPopupAnnouncements([]);
    };

    useEffect(() => {
        if (!loginName) return;
        const showPopupForUnviewedAnnouncements = (fetchedAnnouncements: Announcement[]) => {
            if (hasBlockingSuggestions) {
                console.log('Blocking suggestions exist - skipping popup announcements');
                return;
            }
            const viewedIds = getViewedAnnouncements();
            const unviewed = fetchedAnnouncements.filter(a => a.Popup && !viewedIds.includes(a.Id));
            if (unviewed.length > 0) {
                setPopupAnnouncements(unviewed);
                setIsModalOpen(true);
            }
        };
        const fetchAnnouncements = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getAnnouncements(api, loginName);
                const fetched = response.data.value || [];
                setAnnouncements(fetched);
                showPopupForUnviewedAnnouncements(fetched);
            } catch (err: any) {
                console.error('Error fetching announcements:', err);
                setError(err?.message || 'Failed to load announcements');
            } finally {
                setLoading(false);
            }
        };
        fetchAnnouncements();
    }, [loginName, api, hasBlockingSuggestions]);


    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateString;
        }
    };

    return (
        <div className="m-2 md:m-8 p-4 max-h-[calc(80vh)] flex flex-col rounded-lg border border-border bg-card announcements">
            <span className="text-lg font-semibold mb-4">Announcements</span>

            <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="flex flex-col gap-4">
                    {loading && (
                        <div className="flex justify-center p-8">
                            <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                                <span className="text-sm text-muted-foreground">Loading announcements...</span>
                            </div>
                        </div>
                    )}

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {!loading && !error && announcements.length === 0 && (
                        <p className="text-muted-foreground p-4 text-center text-sm">
                            No announcements at this time.
                        </p>
                    )}

                    {!loading && !error && announcements.length > 0 && (
                        <div className="flex flex-col gap-3">
                            {announcements.map((announcement) => (
                                <div key={announcement.Id} className="rounded-md border border-border p-4">
                                    <span className="text-base font-semibold block mb-2">{announcement.Title}</span>

                                    {announcement.ImageUrl && (
                                        <img
                                            src={announcement.ImageUrl}
                                            alt={announcement.ImageUrlText || announcement.Title}
                                            className="float-right max-w-[40%] max-h-[300px] object-contain rounded ml-3 mb-2"
                                        />
                                    )}

                                    <div
                                        className="text-sm text-foreground [&_a]:underline"
                                        dangerouslySetInnerHTML={{ __html: announcement.Body }}
                                    />

                                    <div className="clear-both pt-2 flex gap-3">
                                        <span className="text-xs text-muted-foreground">Posted: {formatDate(announcement.Created)}</span>
                                        {announcement.Expiration && (
                                            <span className="text-xs text-muted-foreground">Expires: {formatDate(announcement.Expiration)}</span>
                                        )}
                                        <span className="text-xs text-muted-foreground">By: {announcement.CreatedBy}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleModalClose()}>
                <DialogContent className="max-w-[700px] max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Important Announcements ({popupAnnouncements.length})</DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
                        <div className="flex flex-col gap-5">
                            {popupAnnouncements.map((announcement) => (
                                <div key={announcement.Id} className="rounded-lg p-6 bg-muted">
                                    <span className="text-lg font-semibold block mb-3 text-primary">
                                        {announcement.Title}
                                    </span>

                                    {announcement.ImageUrl && (
                                        <img
                                            src={announcement.ImageUrl}
                                            alt={announcement.ImageUrlText || announcement.Title}
                                            className="w-full max-h-[250px] object-contain rounded mb-4"
                                        />
                                    )}

                                    <div
                                        className="text-sm text-foreground mb-4 [&_a]:underline"
                                        dangerouslySetInnerHTML={{ __html: announcement.Body }}
                                    />

                                    <div className="flex gap-3 text-xs text-muted-foreground border-t border-border pt-2">
                                        <span>Posted: {formatDate(announcement.Created)}</span>
                                        {announcement.Expiration && (
                                            <span>Expires: {formatDate(announcement.Expiration)}</span>
                                        )}
                                        <span>By: {announcement.CreatedBy}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button onClick={handleModalClose}>Mark all as read</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AnnouncementsCard;
